import Box from '@mui/material/Box';
import imageCompression from 'browser-image-compression';
import ChatPanelContext, {
  MessagesLoadingContext,
  VoiceRecordingContext,
} from 'components/@home/@messages/ChatPanel/ChatPanelContext';
import ForwardMessagesModal from 'components/@home/@messages/ChatPanel/ForwardMessagesModal';
import JoinToParticipate from 'components/@home/@messages/ChatPanel/JoinToParticipate';
import PinnedMessage from 'components/@home/@messages/ChatPanel/PinnedMessage';
import ImageUploadPreview from 'components/@home/@messages/ChatPanel/SendForm/ImageUploadPreview';
import NoChannelPane from 'components/@home/@messages/NoChannelPane';
import ErrorAlert from 'components/alerts/ErrorAlert';
import deferRender from 'defer-render-hoc';
import isEmpty from 'lodash/isEmpty';
import { bool, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { Outlet, useMatch, useNavigate } from 'react-router';
import { messagesApiUtil } from 'store/app/entities/MessagesApiSlice';
import authCompanyId from 'store/selectors/authCompanyId';
import currentChannelIdSelector from 'store/selectors/currentChannelId';
import isImpersonatingSelector from 'store/selectors/isImpersonating';
import Attachments from 'services/api/Attachments';
import channelShape from 'shapes/channel';
import uniqid from 'uniqid';
import { revokePreviewObjectUrls } from 'utils/compressImages';
import parseFileName from 'utils/getFileExtension';
import useVoiceRecorder from 'utils/useVoiceRecorder';
import { hydrateVoiceMessagePlaybackRateIndex } from 'utils/voiceMessagePlaybackRatePreference';
import { encodePeaksToBase64 } from 'utils/voiceMessageWaveformPeaks';
import ChatHeader from './ChatHeader';
import connector from './connector';
import MessagesList from './MessagesList';
import MessagesWrapper from './MessagesWrapper';
import ReadOnly from './ReadOnly';
import SendForm from './SendForm';

const ChatPanel = ({
  channel = null,
  isCompanyAdmin,
  isMember = false,
  actions,
  locale,
  authEmployee,
}) => {
  const navigate = useNavigate();
  const currentId = useSelector(currentChannelIdSelector);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const messagesLoadingValue = useMemo(
    () => ({ loadingMoreMessages, setLoadingMoreMessages }),
    [loadingMoreMessages],
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [imagesToUpload, setImagesToUpload] = useState([]);
  const [isUploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [attachmentInitiativeTag, setAttachmentInitiativeTag] = useState(null);
  const [sentAsChannel, setSentAsChannel] = useState(false);
  const cancelRef = useRef(() => {});
  const dispatch = useDispatch();
  const companyId = useSelector(authCompanyId);
  const isImpersonating = useSelector(isImpersonatingSelector);
  const messagesListRef = useRef();
  const scrollChatToBottom = useCallback(anchorMessageId => {
    messagesListRef.current?.scrollToBottom?.(anchorMessageId);
  }, []);
  const voiceRecorder = useVoiceRecorder();

  useEffect(() => {
    void hydrateVoiceMessagePlaybackRateIndex().catch(() => {});
  }, []);

  const handleUnpin = async message => {
    await actions.messages.unpin(message);
  };

  const sendMessage = useCallback(
    async ({ text = '', mentions, attachments, initiatives = [], clientMessageId }) => {
      const msg = {
        _id: clientMessageId || uniqid(),
        text,
        mentions:
          mentions?.map(m => ({
            pos: [m.plainTextIndex, m.plainTextIndex + m.display.length - 1],
            employee: m.id,
          })) || [],
        title: undefined,
        acknowledges: [],
        attachments,
        channel_id: channel._id,
        company_id: channel.company,
        previews: {},
        createdAt: Date.now(),
        employee_id: authEmployee?._id,
        user_id: authEmployee?.user?._id,
        isDeletedBy: [],
        isDraft: false,
        isEdited: false,
        isMandatory: false,
        temp: true,
        seen: false,
        senderWasDeleted: false,
        sentAsChannel,
        initiatives,
      };

      await actions.messages.send(msg);
    },
    [
      actions.messages,
      authEmployee?._id,
      authEmployee?.user?._id,
      channel?._id,
      channel?.company,
      sentAsChannel,
    ],
  );

  const onUploadProgress =
    idx =>
    ({ total, loaded }) => {
      setUploadProgress(p => ({ ...p, [idx]: loaded / total }));
    };

  const getCancelFunc = onCancel => {
    cancelRef.current = onCancel;
  };

  function dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    return new Blob([ab], { type: mimeString });
  }

  const upload = useCallback(
    async ({ file, src, caption, meta }, idx) => {
      const { type } = file;
      let compressedFile;
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        fileType: 'image/jpeg',
      };
      if (type.startsWith('image/') && type !== 'image/gif') {
        const { name } = parseFileName(file.name);
        const newFileName = `${name}.jpg`;
        const blob = dataURItoBlob(src);
        compressedFile = new File([blob], newFileName, {
          type: 'image/jpeg',
        });
        compressedFile = await imageCompression(compressedFile, options);
      }
      const uploadOptions = {};
      if (meta) {
        uploadOptions.meta = meta;
      }
      const attachment = await Attachments.upload(
        companyId,
        channel._id,
        compressedFile || file,
        locale,
        onUploadProgress(idx),
        getCancelFunc,
        undefined,
        uploadOptions,
      );
      const text = caption?.markup ?? caption?.text ?? caption ?? '';
      const mentions = caption?.mentions ?? [];
      return { attachments: [attachment], text, mentions };
    },
    [channel?._id, companyId, locale],
  );

  const handleError = error => {
    let msg = I18n.t('AttachFile.Errors.Unknown');
    setTimeout(() => setErrorMessage(''), 15000);
    if (error.data?.type === 'ValidationError') {
      msg = error.data.message;
    } else if (error.message === 'Canceled') {
      msg = I18n.t('AttachFile.Errors.Canceled');
    }
    setUploading(false);
    setErrorMessage(msg);
  };

  const handleSend = useCallback(async () => {
    try {
      setUploading(true);
      const initiatives = attachmentInitiativeTag ? [attachmentInitiativeTag.id] : [];
      const messages = await Promise.all(imagesToUpload.map(upload));

      for (const message of messages) {
        await sendMessage({ ...message, initiatives });
      }
    } catch (error) {
      console.log(error);
      handleError(error);
    } finally {
      revokePreviewObjectUrls(imagesToUpload);
      setImagesToUpload([]);
      setAttachmentInitiativeTag(null);
      setUploading(false);
    }
  }, [attachmentInitiativeTag, imagesToUpload, sendMessage, upload]);

  const setProps = useCallback(
    (props, idx) => {
      setImagesToUpload(images => {
        const newImages = [...images];
        newImages[idx] = { ...newImages[idx], ...props };
        return newImages;
      });
    },
    [setImagesToUpload],
  );

  const stopAndSendVoice = useCallback(async () => {
    let previewUrl = null;
    let tempMessageId = null;
    try {
      const result = await voiceRecorder.stopRecording();
      if (!result?.blob || result.blob.size < 1) {
        setErrorMessage(I18n.t('SendMessageForm.Voice recording empty'));
        setTimeout(() => setErrorMessage(''), 8000);
        return;
      }
      const { blob, peaks, uploadMime, ext } = result;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `voice_${timestamp}.${ext}`, { type: uploadMime });
      tempMessageId = uniqid();
      previewUrl = URL.createObjectURL(blob);

      const optimisticAttachment = {
        _id: uniqid(),
        httpLink: previewUrl,
        meta: { waveformData: encodePeaksToBase64(peaks) },
        mimeType: uploadMime,
        name: file.name,
        size: blob.size,
      };

      dispatch(
        messagesApiUtil.updateQueryData('listMessages', { channelId: channel._id }, draft => {
          if (!draft?.messages || draft.messages.some(m => m._id === tempMessageId)) return;
          draft.messages.unshift({
            _id: tempMessageId,
            text: '',
            mentions: [],
            title: undefined,
            acknowledges: [],
            attachments: [optimisticAttachment],
            channel_id: channel._id,
            company_id: channel.company,
            previews: {},
            createdAt: Date.now(),
            employee_id: authEmployee?._id,
            user_id: authEmployee?.user?._id,
            isDeletedBy: [],
            isDraft: false,
            isEdited: false,
            isMandatory: false,
            temp: true,
            seen: false,
            senderWasDeleted: false,
            sentAsChannel,
            initiatives: [],
            from: authEmployee,
            employee: authEmployee,
          });
        }),
      );
      scrollChatToBottom(tempMessageId);

      setUploading(true);
      const attachment = await Attachments.upload(
        companyId,
        channel._id,
        file,
        locale,
        () => {},
        () => {},
        undefined,
        { meta: { waveformData: encodePeaksToBase64(peaks) } },
      );
      await sendMessage({
        text: '',
        mentions: [],
        attachments: [attachment],
        initiatives: [],
        clientMessageId: tempMessageId,
      });
      scrollChatToBottom(tempMessageId);

      requestAnimationFrame(() => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          previewUrl = null;
        }
      });
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (tempMessageId) {
        dispatch(
          messagesApiUtil.updateQueryData('listMessages', { channelId: channel._id }, draft => {
            if (!draft?.messages) return;
            const idx = draft.messages.findIndex(m => m._id === tempMessageId);
            if (idx >= 0) draft.messages.splice(idx, 1);
          }),
        );
      }
      handleError(error);
    } finally {
      setUploading(false);
    }
  }, [
    authEmployee,
    channel?._id,
    channel?.company,
    companyId,
    dispatch,
    locale,
    sendMessage,
    sentAsChannel,
    voiceRecorder,
    scrollChatToBottom,
  ]);

  const voiceRecordingValue = useMemo(
    () => ({
      isRecording: voiceRecorder.isRecording,
      isPaused: voiceRecorder.isPaused,
      recordingTime: voiceRecorder.recordingTime,
      waveformHeights: voiceRecorder.waveformHeights,
      previewRecordingUrl: voiceRecorder.previewRecordingUrl,
      previewWaveformHeights: voiceRecorder.previewWaveformHeights,
      startRecording: voiceRecorder.startRecording,
      stopAndSend: stopAndSendVoice,
      cancelRecording: voiceRecorder.cancelRecording,
      pauseRecording: voiceRecorder.pauseRecording,
      resumeRecording: voiceRecorder.resumeRecording,
    }),
    [
      voiceRecorder.isRecording,
      voiceRecorder.isPaused,
      voiceRecorder.recordingTime,
      voiceRecorder.waveformHeights,
      voiceRecorder.previewRecordingUrl,
      voiceRecorder.previewWaveformHeights,
      voiceRecorder.startRecording,
      voiceRecorder.cancelRecording,
      voiceRecorder.pauseRecording,
      voiceRecorder.resumeRecording,
      stopAndSendVoice,
    ],
  );

  const scrollToMessage = async message => {
    const messageId = message._id;
    navigate(`/home/messages/${channel._id}`, { state: { messageId } });
    const loadMessages =
      (!messageId && channel && !channel.complete) ||
      (messageId && !channel.messages_ids.includes(messageId));
    setTimeout(() => {
      if (loadMessages) {
        actions.messages.load(channel._id, { aroundId: messageId });
      }
      messagesListRef.current?.scrollToSelectedMessage();
    }, 10);
  };

  const inArticle = useMatch('/home/messages/:id/article/:articleId');

  if (!channel || !currentId) {
    return <NoChannelPane />;
  }

  const progressArr = Object.values(uploadProgress);
  const progress = (progressArr.reduce((acc, curr) => acc + curr, 0) / progressArr.length) * 100;

  return (
    <>
      <Outlet />
      <Box
        sx={theme =>
          inArticle
            ? { display: 'none' }
            : {
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? theme.palette.secondary.ultraLight
                    : theme.palette.secondary.extraLight,
              }
        }
      >
        <MessagesLoadingContext.Provider value={messagesLoadingValue}>
          <ChatPanelContext.Provider
            value={{
              imagesToUpload,
              setImagesToUpload,
              setProps,
              attachmentInitiativeTag,
              setAttachmentInitiativeTag,
            }}
          >
            <VoiceRecordingContext.Provider value={voiceRecordingValue}>
              <div>
                <ChatHeader />
                <PinnedMessage
                  onClick={scrollToMessage}
                  message={channel.pinnedMessage}
                  onUnpin={handleUnpin}
                  canUnpin={channel.isAdmin || channel.isDirect}
                />
              </div>
              {isEmpty(imagesToUpload) ? (
                <>
                  <MessagesWrapper>
                    <MessagesList ref={messagesListRef} />
                  </MessagesWrapper>
                  {isCompanyAdmin && !isMember && <JoinToParticipate />}
                  {isImpersonating ? (
                    <ReadOnly message={I18n.t('Impersonation.ReadOnlyComposer')} />
                  ) : channel.isReadOnly ? (
                    <ReadOnly />
                  ) : (
                    <SendForm
                      onChangeSentAsChannel={setSentAsChannel}
                      scrollChatToBottom={scrollChatToBottom}
                    />
                  )}
                  {!isImpersonating &&
                    (isCompanyAdmin || channel.isAdmin || channel.canWriteAsChannel) &&
                    channel.isReadOnly && (
                      <SendForm
                        onChangeSentAsChannel={setSentAsChannel}
                        scrollChatToBottom={scrollChatToBottom}
                      />
                    )}
                </>
              ) : (
                <ImageUploadPreview
                  onSend={handleSend}
                  isUploading={isUploading}
                  progress={progress}
                />
              )}
              <ErrorAlert isOpen={!!errorMessage} label={I18n.t('AttachFile.Errors.Title')}>
                {errorMessage}
              </ErrorAlert>
              <ForwardMessagesModal />
            </VoiceRecordingContext.Provider>
          </ChatPanelContext.Provider>
        </MessagesLoadingContext.Provider>
      </Box>
    </>
  );
};

ChatPanel.propTypes = {
  channel: channelShape,
  isCompanyAdmin: bool.isRequired,
  isMember: bool,
  actions: object.isRequired,
  locale: string.isRequired,
  authEmployee: object.isRequired,
};

export default deferRender(connector(ChatPanel));
